import { useModal, create as createNiceModal } from '@ebay/nice-modal-react';
import { useCallback, useMemo } from 'react';
import { Stack } from '@mui/material';
import { FormattedMessage } from 'react-intl';

import { buttonTextMessages } from '@ocx-app/modules/localization/button-text.messages';

import { PaymentAccountType } from '@ocx/graphql';
import { Button, SizingMultiplier } from '@ocx/ui';
import { logInDev } from '@ocx/utils';
import { ModalDrawer } from '@ocx/ui-modals';
import { useConfiguration } from '@ocx/data-configuration';

import {
  AddPaymentInstrumentPopupController,
  AddPaymentInstrumentPopupProps,
} from './add-payment-instrument-popup.controller';

const AddPaymentInstrumentPopupModal = createNiceModal(AddPaymentInstrumentPopupController);

type PaymentInstrumentSelectorModalProps = {
  onCancel(choice: Extract<PaymentAccountType, 'PREPAID' | 'CREDIT'> | 'cancel'): void;
};
const PaymentInstrumentSelectorModal = createNiceModal(({ onCancel }: PaymentInstrumentSelectorModalProps) => {
  const { visible, hide, remove } = useModal();
  const handleExited = useCallback(() => {
    // onClosed?.();
    remove();
  }, [remove]);

  const handleCancel = useCallback(() => {
    onCancel('cancel');
    hide().catch(logInDev);
  }, [hide, onCancel]);

  const handleJustCard = useCallback(() => {
    onCancel(PaymentAccountType.Credit);
    hide().catch(logInDev);
  }, [hide, onCancel]);

  const handlePrepaidCard = useCallback(() => {
    onCancel(PaymentAccountType.Prepaid);
    hide().catch(logInDev);
  }, [hide, onCancel]);

  return (
    <ModalDrawer open={visible} onClose={handleCancel} onExited={handleExited}>
      <Stack spacing={SizingMultiplier.md} p={SizingMultiplier.lg} pt={SizingMultiplier.xl}>
        <Button onClick={handleJustCard}>
          <FormattedMessage
            id="payment-instrument-account-type-selector:credit:button-text"
            defaultMessage="credit or debit card"
            description="Button text for Credit card"
          />
        </Button>
        <Button onClick={handlePrepaidCard}>
          <FormattedMessage
            id="payment-instrument-account-type-selector:prepaid:button-text"
            defaultMessage="gift card"
            description="Button text for Prepaid card"
          />
        </Button>
        <Button onClick={handleCancel} variant="text">
          <FormattedMessage {...buttonTextMessages['button-text:close']} />
        </Button>
      </Stack>
    </ModalDrawer>
  );
});

export const useAddPaymentInstrumentPopup = () => {
  const isPrepaidCardEnabled = useConfiguration('wallet.prepaidAccountTypeEnabled');
  const { show: showAddPaymentInstrument } = useModal(AddPaymentInstrumentPopupModal);
  const { show: showPreselect } = useModal(PaymentInstrumentSelectorModal);

  const open = useCallback(
    (props: Omit<AddPaymentInstrumentPopupProps, 'type'> = {}) => {
      if (!isPrepaidCardEnabled) {
        showAddPaymentInstrument({ onCreated: props.onCreated, type: PaymentAccountType.Credit });
        return;
      }

      showPreselect({
        onCancel: (choice) => {
          if (choice !== 'cancel') {
            showAddPaymentInstrument({ onCreated: props.onCreated, type: choice });
          }
        },
      });
    },
    [isPrepaidCardEnabled, showAddPaymentInstrument, showPreselect],
  );
  return useMemo(() => ({ open }), [open]);
};
